import React from 'react';
import { Entity } from '@backstage/catalog-model';
import {
  EmptyState,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useEntityList } from '@backstage/plugin-catalog-react';
import { makeStyles } from '@material-ui/core';
import { TeamCard } from './TeamCard';
import { useServiceProviderFacts } from './useServiceProviderFacts';

const useStyles = makeStyles(theme => ({
  // As many columns as fit, none narrower than a card can be read at. A CSS
  // grid rather than MUI's: its spacing is a gap, where MUI's is a negative
  // margin that pushed the last column past the edge of the page.
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: theme.spacing(2),
    alignItems: 'stretch',
  },
}));

/**
 * Props for {@link TeamCardGrid}.
 *
 * @public
 */
export type TeamCardGridProps = {
  /** The titles of the departments by vuid, see useDepartmentTitles. */
  departmentTitles: Record<string, string | undefined>;
};

const titleOf = (entity: Entity) =>
  entity.metadata.title ?? entity.metadata.name;

/**
 * The teams the surrounding listing holds, a card each, in one gallery sorted
 * by the name of the team. Each card names the department above the team; the
 * department filter narrows the gallery down to one.
 *
 * @public
 */
export const TeamCardGrid = (props: Readonly<TeamCardGridProps>) => {
  const { departmentTitles } = props;
  const classes = useStyles();
  const { entities, loading, error } = useEntityList();
  const { factsOf, profileOf } = useServiceProviderFacts(entities);

  if (loading) {
    return <Progress />;
  }
  if (error) {
    return <ResponseErrorPanel error={error} />;
  }
  if (entities.length === 0) {
    return (
      <EmptyState
        missing="data"
        title="No teams found"
        description="No team matches the filters."
      />
    );
  }

  const departmentOf = (team: Entity) => {
    const parent = team.spec?.parent as string | undefined;
    return parent ? (departmentTitles[parent] ?? parent) : undefined;
  };
  const sorted = [...entities].sort((a, b) =>
    titleOf(a).localeCompare(titleOf(b)),
  );

  return (
    <div className={classes.grid}>
      {sorted.map(team => (
        <TeamCard
          key={team.metadata.name}
          entity={team}
          department={departmentOf(team)}
          facts={factsOf(team)}
          profileOf={profileOf}
        />
      ))}
    </div>
  );
};
